import { useAuth } from '@/hooks';
import { useField } from 'formik';
import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import Select from 'react-select';
import { getCustomStyles } from '@/theme/components/select';
import {
  Box,
  Flex,
  FormControl,
  FormErrorMessage,
  Tooltip,
} from '@chakra-ui/react';
import { FormCustomLabel } from '@/components/formBuilder/components';
import { components } from 'react-select';
import { KImage, KText } from '@/ui-library/components';
import isEqual from 'lodash.isequal';

interface Option {
  value: string;
  label: string;
  avatar?: string;
  zoom_rate_limit_reached?: boolean;
  can_host_zoom_meeting?: boolean;
}

const ZOOM_PLAN_TYPE = {
  1: 'Free user', //basic
  2: 'Paid user', // paid
  4: 'Unassigned without Meetings Basic', //Unassigned without Meetings Basic
  99: 'None', // None (only with ssoCreate)
};

const isOptionDisabled = (option) => {
  const {
    can_host_zoom_meeting: canHost,
    zoom_rate_limit_reached: limitReached,
  } = option ?? {};
  if (canHost === false) {
    return true;
  } else if (canHost === true) {
    if (limitReached === true) {
      return true;
    }
    return false;
  } else {
    return false;
  }
};

const getZoomInfo = (data) => {
  const { can_host_zoom_meeting, zoom_rate_limit_reached, zoom_plan } =
    data ?? {};

  let hostInfo = { label: '', color: '', bgColor: '' };

  if (can_host_zoom_meeting === false) {
    hostInfo = {
      label: 'Not part of Zoom account',
      color: 'pastel-1-dark',
      bgColor: 'pastel-1-light',
    };
  } else if (zoom_rate_limit_reached) {
    hostInfo = {
      label: 'Zoom limit reached',
      color: 'pastel-1-dark',
      bgColor: 'pastel-1-light',
    };
  } else if ([4, 99].includes(Number(zoom_plan))) {
    hostInfo = {
      label: 'Needs Zoom license',
      color: 'secondary-700',
      bgColor: 'secondary-50',
    };
  } else if (zoom_plan && ZOOM_PLAN_TYPE[Number(zoom_plan)]) {
    hostInfo = {
      label: ZOOM_PLAN_TYPE[zoom_plan],
      color: 'secondary-700',
      bgColor: 'secondary-50',
    };
  }

  return hostInfo;
};

const RenderUserInfo = ({ data, isDisabled }) => {
  return (
    <Flex
      gap="5px"
      alignItems="center"
      justifyContent="space-between"
      overflow="hidden"
      minW="90px"
      w="100%"
      flex={1}
      opacity={isDisabled ? 0.5 : 1}
      cursor={isDisabled ? 'not-allowed' : 'pointer'}
    >
      <KImage src={data?.avatar} name={data?.label || data?.name} />
      <KText flex={1} maxWidth="100%" flexShrink={1} isTruncated>
        {data?.label || data?.name}
      </KText>
    </Flex>
  );
};

const CustomSingleValue = (props) => {
  const hostInfo = getZoomInfo(props?.data);
  const isDisabled = isOptionDisabled(props?.data);

  return (
    <components.SingleValue {...props}>
      <Flex gap="6px" flexWrap="wrap" w="100%">
        <RenderUserInfo data={props?.data} isDisabled={isDisabled} />
        {hostInfo?.label && (
          <KText
            variant="small_text"
            color={hostInfo?.color}
            bgColor={hostInfo?.bgColor}
            borderRadius="4px"
            padding="4px 8px"
            w="fit-content"
          >
            {hostInfo?.label}
          </KText>
        )}
      </Flex>
    </components.SingleValue>
  );
};

const CustomOption = memo(({ data, label, ...props }: any) => {
  let hostInfo = getZoomInfo(data);
  const disabled = isOptionDisabled(data);

  return (
    <components.Option {...props}>
      <Flex gap="6px" flexWrap="wrap" w="98%">
        <RenderUserInfo data={data} isDisabled={disabled} />
        {hostInfo?.label && (
          <KText
            variant="small_text"
            color={hostInfo?.color}
            bgColor={hostInfo?.bgColor}
            borderRadius="4px"
            padding="4px 8px"
            w="fit-content"
          >
            {hostInfo?.label}
          </KText>
        )}
      </Flex>
    </components.Option>
  );
});

export const SchZoomHost = ({
  interviewPlan,
  isEdit,
  registerForUpdates,
  interviewDataRef,
  isSelfSchedule,
  updateSharedData,
}) => {
  const [, meta, helpers] = useField('host_id');
  const { value, error, touched } = meta;
  const { setTouched, setValue, setError } = helpers;
  const isError =
    touched && error && (Array.isArray(value) ? value?.length === 0 : !value);

  const [localInterviewKind, setLocalInterviewKind] = useState(
    interviewDataRef?.current?.interviewKind
  );

  const [hostOptions, setHostOptions] = useState([]);
  const [hostOptionsReady, setHostOptionsReady] = useState(false);

  // Ensure hostOptions is updated with the latest interviewersDetails on mount/remount
  useEffect(() => {
    let isMounted = true;
    const updateHostOptions = () => {
      if (!isMounted) return;
      if (Array.isArray(interviewDataRef?.current?.interviewersDetails)) {
        const newHostOptions = interviewDataRef.current.interviewersDetails.map(
          (interviewer) => ({
            ...interviewer,
            value: interviewer?.id,
            label: interviewer?.name,
          })
        );
        setHostOptions(newHostOptions);
        setHostOptionsReady(true);
      } else {
        setHostOptions([]);
        setHostOptionsReady(true);
      }
    };

    updateHostOptions();

    return () => {
      isMounted = false;
      setHostOptions([]);
    };
  }, [interviewDataRef]);

  useEffect(() => {
    const unregisterInterviewKind = registerForUpdates(
      'interviewKind',
      (data) => {
        const { interviewKind } = data?.fullData ?? {};

        setLocalInterviewKind((prev) => {
          return isEqual(prev, interviewKind) ? prev : interviewKind;
        });
      }
    );

    return () => {
      unregisterInterviewKind();
    };
  }, [registerForUpdates]);

  useEffect(() => {
    const unregisterInterviewers = registerForUpdates(
      'interviewersDetails',
      (data) => {
        const { interviewersDetails } = data?.fullData ?? {};
        const newHostOptions = interviewersDetails?.map((interviewer) => ({
          ...interviewer,
          value: interviewer?.id,
          label: interviewer?.name,
        }));
        setHostOptions((prev) => {
          return isEqual(prev, newHostOptions) ? prev : newHostOptions;
        });
        setHostOptionsReady(true);
      }
    );

    return () => {
      unregisterInterviewers();
    };
  }, [registerForUpdates]);

  const [selectOption, setSelectOption] = useState<Option[]>([]);
  const selectRef = useRef<any>(null);
  const { currUser } = useAuth();

  const isDisabled = useMemo(() => {
    return (
      isSelfSchedule &&
      localInterviewKind === 'one_on_one' &&
      hostOptions?.length > 1
    );
  }, [isSelfSchedule, localInterviewKind, hostOptions]);

  // Sync selectOption with Formik value to prevent clearing on render
  useEffect(() => {
    // Auto-select host when Zoom is selected and no host is set
    if (!value && selectOption.length === 0 && hostOptionsReady) {
      // 1) If exactly one interviewer is selected and can host, prefer them
      if (hostOptions?.length === 1 && !isOptionDisabled(hostOptions[0])) {
        const onlyHost: any = hostOptions[0];
        const selectedId = onlyHost?.id ?? onlyHost?.value;
        setSelectOption([
          {
            ...onlyHost,
            value: selectedId,
            label: onlyHost?.name ?? onlyHost?.label,
          },
        ]);
        setValue(selectedId);
        setTouched(true);
        if (updateSharedData) updateSharedData('hostId', selectedId);
      }
      // 2) Otherwise, if current user can host, select current user
      else if (interviewPlan?.can_host_zoom_meeting && currUser?.id) {
        const autoHostOption = {
          ...currUser,
          value: currUser?.id,
          label:
            (currUser?.first_name ?? '') + ' ' + (currUser?.last_name ?? ''),
          can_host_zoom_meeting: interviewPlan?.can_host_zoom_meeting,
          zoom_rate_limit_reached: interviewPlan?.zoom_rate_limit_reached,
          zoom_plan: interviewPlan?.zoom_plan,
        } as any;
        setSelectOption([autoHostOption]);
        setValue(currUser?.id);
        setTouched(true);
        if (updateSharedData) {
          updateSharedData('hostId', currUser?.id);
        }
      }
    }

    // If we initially auto-set current user but later exactly one valid interviewer exists, prefer that interviewer
    //Removing value === currUser?.id condition here because we are setting the host (if there is only one user in the if condition (selectedId !== value))-> issue [ENG-1349])
    if (
      value == null &&
      hostOptions?.length === 1 &&
      !isOptionDisabled(hostOptions[0])
    ) {
      const onlyHost: any = hostOptions[0];
      const selectedId = onlyHost?.id ?? onlyHost?.value;
      if (selectedId !== value) {
        setSelectOption([
          {
            ...onlyHost,
            value: selectedId,
            label: onlyHost?.name ?? onlyHost?.label,
          },
        ]);
        setValue(selectedId);
        setTouched(true);
        if (updateSharedData) updateSharedData('hostId', selectedId);
      }
    }

    if (value && !selectOption.length) {
      const currentHost = Array.isArray(hostOptions)
        ? hostOptions.find(
            (option) => option?.value === value || option?.id === value
          )
        : null;
      if (currentHost) {
        setSelectOption([
          {
            ...currentHost,
            value: currentHost?.id ?? currentHost?.value,
            label: currentHost?.name ?? currentHost?.label,
          },
        ]);
      } else if (
        interviewPlan?.user_zoom_status &&
        (interviewPlan.user_zoom_status.id === value ||
          interviewPlan.user_zoom_status.value === value)
      ) {
        const userZoomStatus = interviewPlan.user_zoom_status;
        setSelectOption([
          {
            ...userZoomStatus,
            value: userZoomStatus.id,
            label: userZoomStatus.name,
          },
        ]);
      } else if (
        value === currUser?.id &&
        interviewPlan?.can_host_zoom_meeting
      ) {
        setSelectOption([
          {
            ...currUser,
            value: currUser?.id,
            label:
              (currUser?.first_name ?? '') + ' ' + (currUser?.last_name ?? ''),
            can_host_zoom_meeting: interviewPlan?.can_host_zoom_meeting,
            zoom_rate_limit_reached: interviewPlan?.zoom_rate_limit_reached,
            zoom_plan: interviewPlan?.zoom_plan,
          },
        ]);
      }
    }
    // If isDisabled is true, always set current user as host
    if (
      isDisabled &&
      value !== currUser?.id &&
      interviewPlan?.can_host_zoom_meeting
    ) {
      setSelectOption([
        {
          ...currUser,
          value: currUser?.id,
          label:
            (currUser?.first_name ?? '') + ' ' + (currUser?.last_name ?? ''),
          can_host_zoom_meeting: interviewPlan?.can_host_zoom_meeting,
          zoom_rate_limit_reached: interviewPlan?.zoom_rate_limit_reached,
          zoom_plan: interviewPlan?.zoom_plan,
        },
      ]);
      setValue(currUser?.id);
      setTouched(true);
    }
    // Clear selectOption and Formik value if selected option is no longer in hostOptions or user_zoom_status
    if (selectOption.length > 0) {
      const selectedValue = selectOption[0].value;
      const isOptionStillAvailableInHostOptions = hostOptions.some(
        (option) =>
          option.value === selectedValue || option.id === selectedValue
      );
      const isOptionStillAvailableInUserZoomStatus =
        interviewPlan?.user_zoom_status &&
        (interviewPlan.user_zoom_status.id === selectedValue ||
          interviewPlan.user_zoom_status.value === selectedValue);

      if (
        // in edit case check for both hostOptions and user_zoom_status
        isEdit
          ? !isOptionStillAvailableInHostOptions &&
            !isOptionStillAvailableInUserZoomStatus
          : // in create case check for hostOptions and curr user
            !isOptionStillAvailableInHostOptions &&
            currUser?.id !== selectedValue
      ) {
        setSelectOption([]);
        setValue(undefined);
        setError('Host is required');
      }
    }
  }, [
    value,
    isEdit,
    hostOptions,
    hostOptionsReady,
    interviewPlan,
    setValue,
    setError,
    selectOption,
    currUser,
    isDisabled,
    setTouched,
    updateSharedData,
  ]);

  const handleChange = useCallback(
    (selectedOption: Option | null) => {
      const newValue = selectedOption?.value || null;
      const newSelectedOption = selectedOption ? [selectedOption] : [];
      setSelectOption(newSelectedOption);
      setTouched(true);
      setError(null);
      setValue(newValue);

      // Store in shared data so it's available when switching interviews
      if (updateSharedData) {
        updateSharedData('hostId', newValue);
      }
    },
    [setTouched, setError, setValue, updateSharedData]
  );

  const hostHasCurrentUser = hostOptions?.some(
    (option) => option?.id === currUser?.id
  );

  const currentUserOption = hostHasCurrentUser
    ? false
    : isEdit
    ? {
        ...interviewPlan?.user_zoom_status,
        value: interviewPlan?.user_zoom_status?.id,
        label: interviewPlan?.user_zoom_status?.name,
      }
    : {
        id: currUser?.id,
        value: currUser?.id,
        label: (currUser?.first_name ?? '') + ' ' + (currUser?.last_name ?? ''),
        avatar: currUser?.avatar,
        can_host_zoom_meeting: interviewPlan?.can_host_zoom_meeting,
        zoom_rate_limit_reached: interviewPlan?.zoom_rate_limit_reached,
        zoom_plan: interviewPlan?.zoom_plan,
      };

  return (
    <FormControl
      isRequired
      isInvalid={touched && !!error}
      h="100%"
      margin="0.75em 0"
    >
      <FormCustomLabel
        item={{
          label: 'Host',
          name: 'host_id',
          labelStyles: { position: 'relative' },
          labelInfoText: `To host, you need to be part of your organization's Zoom account. Only the person scheduling the interview or an interviewer can host.`,
        }}
      />
      <Tooltip
        placement="left"
        label={
          isDisabled &&
          "For one-on-one interviews with multiple interviewers, the host can't be changed because the interviewer is not assigned until the candidate picks a time slot"
        }
      >
        <Box cursor={isDisabled ? 'not-allowed' : 'pointer'}>
          <Select
            inputId="host_id"
            ref={selectRef}
            value={selectOption[0] || null}
            isMulti={false}
            isClearable={false}
            options={[...(hostOptions || []), currentUserOption].filter(
              Boolean
            )}
            isDisabled={isDisabled}
            isOptionDisabled={(option) => isOptionDisabled(option)}
            boxShadow={isError && '0 0 0 2px #A32020'}
            noOptionsMessage={({ inputValue }) => (
              <Flex
                flexDirection="column"
                gap="4px"
                justifyContent="flex-start"
              >
                {inputValue ? (
                  <>
                    <KText variant="small_text">
                      No results found for "{inputValue}".
                    </KText>
                    <KText variant="small_text">
                      Try searching for someone else or add valid participants.
                    </KText>
                  </>
                ) : (
                  <>
                    <KText variant="small_text">No valid host found.</KText>
                    <KText variant="small_text">
                      Add valid participants or choose some other alternative to
                      Zoom.
                    </KText>
                  </>
                )}
              </Flex>
            )}
            styles={getCustomStyles([
              {
                type: 'control',
                newStyles: {
                  boxShadow: isError ? '0 0 0 2px #A32020' : 'none',
                },
              },
              {
                type: 'option',
                newStyles: {
                  opacity: 1,
                },
              },
            ])}
            components={{
              IndicatorSeparator: null,
              Option: CustomOption,
              SingleValue: CustomSingleValue,
            }}
            onChange={handleChange}
            onBlur={() => setTouched(true)}
            placeholder="Select host"
          />
        </Box>
      </Tooltip>
      {isError && (
        <FormErrorMessage flexDir="column" alignItems="flex-start">
          <KText color="ats-negative">{error}</KText>
        </FormErrorMessage>
      )}
    </FormControl>
  );
};
